import React, { useState, useEffect, useMemo } from "react"
import { connect } from "react-redux"
import { useNavigate } from "react-router-dom"
import { motion } from "framer-motion"
import { Send } from "lucide-react"

import styles from "./SendQuotePopup.module.scss"
import ModalOverlay from "../../UI/ModalOverlay/ModalOverlay.tsx"
import CustomSelect from "../../../pages/ProjectDocumentation/components/CustomSelect.tsx"
import { resetSuccess, sendQuote } from "../../../store/actions/categoryActions"
import useGetTodayDate from "../../../hooks/useGetTodayDate"
import { fireAnalytics } from "../../../utils/firebase"
import { JURISDICTIONS } from "../../../constants/jurisdictions.ts"

const variants = {
  visible: {
    scale: 1,
    opacity: 1,
  },
  hidden: {
    scale: 0.95,
    opacity: 0,
  },
}

interface QuoteData {
  name: string
  city: string
  receiveFrom: string
  receiveTo: string
  currency: string
  description: string
  jurisdiccion: string
  zone: string
}

interface SendQuotePopupProps {
  onClose: () => void
  onSuccess: () => void
  sendQuote: (quoteData: any, materials: any[], navigate: ReturnType<typeof useNavigate>) => Promise<void>
  materials: any[]
  setShowPreview: (value: boolean) => void
}

const SendQuotePopup: React.FC<SendQuotePopupProps> = ({
  onClose,
  sendQuote,
  materials,
  onSuccess,
  setShowPreview,
}) => {
  const [isButtonDisabled, setIsButtonDisabled] = useState(true)
  const [quoteData, setQuoteData] = useState<QuoteData>({
    name: "",
    city: "",
    receiveFrom: "",
    receiveTo: "",
    currency: "ARS",
    description: "",
    jurisdiccion: "",
    zone: "",
  })

  const todayDate = useGetTodayDate()
  const navigate = useNavigate()

  const jurisdictionOptions = useMemo(
    () =>
      JURISDICTIONS.map((jurisdiction) => ({
        value: jurisdiction.id.toString(),
        label: jurisdiction.name,
      })),
    [],
  )

  const currencyOptions = useMemo(
    () => [
      { value: "ARS", label: "Pesos argentinos" },
      { value: "USD", label: "Dólares estadounidenses" },
    ],
    [],
  )

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target
    setQuoteData((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const handleJurisdictionChange = (value: string) => {
    setQuoteData((prev) => ({
      ...prev,
      jurisdiccion: value,
    }))
  }

  const handleCurrencyChange = (value: string) => {
    setQuoteData((prev) => ({
      ...prev,
      currency: value,
    }))
  }

  const convertDateToISOFormat = (date: string) => {
    if (!date) {
      return ""
    }
    const [dd, mm, yyyy] = date.split("/")
    return `${yyyy}-${mm}-${dd}`
  }

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    setShowPreview(false)

    try {
      const payload = {
        ...quoteData,
        jurisdiccion: quoteData.jurisdiccion ? Number(quoteData.jurisdiccion) : null,
      }

      await sendQuote(payload, materials, navigate)
      fireAnalytics("send_quotation_finish")
      onSuccess()
      onClose()
    } catch (error) {
      console.error("Error al enviar la cotización:", error)
    }
  }

  useEffect(() => {
    const isValid =
      quoteData.name &&
      quoteData.city &&
      quoteData.jurisdiccion &&
      quoteData.receiveFrom &&
      quoteData.receiveTo &&
      quoteData.description

    setIsButtonDisabled(!isValid)
  }, [quoteData])

  return (
    <ModalOverlay isOpen onClose={onClose} preventClose zIndex={32}>
      <motion.div variants={variants} initial="hidden" animate="visible" className={styles.popup}>
        <div className={styles.popup_header}>
          <h2 className={styles.popup_header_title}>Enviar para cotizar</h2>
          <button type="button" onClick={onClose} className={styles.popup_header_cancelBtn}>
            Cancelar
          </button>
        </div>
        <form className={styles.popup_form} onSubmit={handleSubmit}>
          <div className={styles.formGroup}>
            <label htmlFor="name">Nombre de la cotización</label>
            <input id="name" name="name" type="text" value={quoteData.name} onChange={handleInputChange} required />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="jurisdiccion">Ubicación</label>
            <CustomSelect
              value={quoteData.jurisdiccion}
              onChange={handleJurisdictionChange}
              options={jurisdictionOptions}
              placeholder="Seleccionar una ubicación"
            />
          </div>

  <div className={styles.formGroup}>
            <label htmlFor="city">Ciudad</label>
            <input id="city" name="city" type="text" value={quoteData.city} onChange={handleInputChange} required />
          </div>

          <div className={styles.formGroup_inline}>
            <div className={styles.datePickerWrapper}>
              <label htmlFor="receiveFrom">Recibir desde</label>
              <input
                className={styles.datePicker}
                id="receiveFrom"
                name="receiveFrom"
                type="date"
                value={quoteData.receiveFrom}
                onChange={(event) => {
                  handleInputChange(event)
                  if (quoteData.receiveTo && new Date(event.target.value) > new Date(quoteData.receiveTo)) {
                    setQuoteData((prev) => ({ ...prev, receiveTo: "" }))
                  }
                }}
                max={quoteData.receiveTo || undefined}
                min={convertDateToISOFormat(todayDate)}
                required
              />
            </div>
            <div className={styles.datePickerWrapper}>
              <label htmlFor="receiveTo">Hasta</label>
              <input
                className={styles.datePicker}
                id="receiveTo"
                name="receiveTo"
                type="date"
                value={quoteData.receiveTo}
                onChange={(event) => {
                  handleInputChange(event)
                  if (quoteData.receiveFrom && new Date(event.target.value) < new Date(quoteData.receiveFrom)) {
                    setQuoteData((prev) => ({ ...prev, receiveFrom: "" }))
                  }
                }}
                min={quoteData.receiveFrom || convertDateToISOFormat(todayDate)}
                required
              />
            </div>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="currency">Moneda</label>
            <CustomSelect value={quoteData.currency} onChange={handleCurrencyChange} options={currencyOptions} />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="description">Descripción</label>
            <textarea
              id="description"
              name="description"
              rows={4}
              value={quoteData.description}
              onChange={handleInputChange}
            />
          </div>

          <div className={styles.buttonGroup}>
            <button disabled={isButtonDisabled} type="submit" className={styles.sendBtn}>
              <Send size={16} />
              {isButtonDisabled ? "Completar todos los campos" : "Publicar en edify"}
            </button>
          </div>
        </form>
      </motion.div>
    </ModalOverlay>
  )
}

const mapStateToProps = (state: any) => ({
  success: state.category.success,
  error: state.category.error,
  loading: state.category.loading,
})

export default connect(mapStateToProps, { sendQuote, resetSuccess })(SendQuotePopup)

